import Axios from 'axios';
import { ENDPOINTS } from 'endpoints';
import qs from 'qs';
import { store } from 'redux/storeConfig/store';
import { getClientInfo } from 'utility/logging';

export const getSubdomain = () => {
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case 'development':
      return process.env.REACT_APP_DEVELOPMENT_API_SUBDOMAIN;
    case 'staging':
      return process.env.REACT_APP_STAGING_API_SUBDOMAIN;
    case 'production':
      return process.env.REACT_APP_PRODUCTION_API_SUBDOMAIN;
    case 'demo':
      return process.env.REACT_APP_PRODUCTION_API_SUBDOMAIN;
    default:
      return process.env.REACT_APP_DEVELOPMENT_API_SUBDOMAIN;
  }
};

export const isTimeoutError = (error) => {
  return (
    error.code === 'ECONNABORTED' ||
    error.message?.includes('timeout') ||
    error.message?.includes('Network Error')
  );
};

const axios = Axios.create({ baseURL: `https://${getSubdomain()}.iserviceauto.com/api` });

axios.interceptors.request.use((config) => {
  // Additional info for logging
  config.headers['X-Client-Info'] = JSON.stringify(getClientInfo());

  return config;
});

const setETagHeader = (eTag) => {
  return {
    'If-None-Match': eTag ?? '',
  };
};

const getInvalidETagMessage = (message) => {
  return message.replace('ERROR_ETAG. ', '');
};

const isETagInvalidError = (error) => {
  return !!error.response?.data.message.includes('ERROR_ETAG.');
};

export const handleETagError = (error) => (dispatch) => {
  if (isETagInvalidError(error)) {
    const message = getInvalidETagMessage(error.response?.data.message);
    if (message) {
      dispatch({ type: 'SET_ETAG_ERROR_MESSAGE', data: message });
    }
  }
};

const SIGNED_DOCUMENT_GRANT_HEADER = 'X-Signed-Document-Grant';

const clientApi = {
  _updateBaseUrl: (newBaseUrl) => {
    axios.defaults.baseURL = newBaseUrl;
  },
  getBaseUrl: () => {
    return axios.defaults.baseURL;
  },
  trackCustomCard: (repairOrderId) => {
    return makePostRequest('/repair-order/custom-card', { repairOrderId });
  },
  login: (linkHash, isPayment) => {
    if (isPayment) {
      return makePostRequest('/authentication/authenticate', {
        paymentLinkHash: linkHash,
      });
    }
    return makePostRequest('/authentication/authenticate', { linkHash });
  },
  requestAppraisal: (repairOrderId) => {
    return makePostRequest('/repair-order/appraise-click', {
      repairOrderId,
    });
  },
  logFinanceRequest: (repairOrderId) => {
    return makePostRequest('/repair-order/finance-click', {
      repairOrderId,
    });
  },
  financeRequest: () => {
    return Promise.resolve({ success: true });
  },
  logCouponRequest: (repairOrderId) => {
    return makePostRequest('/repair-order/coupon-click', {
      repairOrderId,
    });
  },
  submitRating: (repairOrderReviewID, rating) => {
    return makePostRequest('/my-review/outcome', {
      repairOrderReviewID,
      rating,
      platform: 'google',
    });
  },
  authorizeVisit: (signature, roId) => {
    return makePostRequest('/repair-order-waiver/signed', {
      repairOrderId: roId,
      signature,
    });
  },
  acknowledgeWaiver: (roId) => {
    return makePostRequest('/repair-order-waiver/acknowledged', {
      repairOrderId: roId,
    });
  },
  createDemo: (params) => {
    return makePostRequest('/demo', params);
  },
  getCustomerPricing: (repairOrderQuoteId, recommendations) => {
    return newMakePostRequest('/repair-order/customer-pricing', {
      repairOrderQuoteId,
      recommendations,
    });
  },
  getDemo: (id) => {
    return makeGetRequest(`/demo/by-repair-order-id/${id}`);
  },
  getRO: () => {
    const linkHash = store.getState().auth.linkHash;
    return makeGetRequest(`/repair-order/link-hash/${linkHash}`);
  },
  getRos: (params) => {
    return makeGetRequest(`/repair-order`, params);
  },
  getSettings: () => {
    return makeGetRequest(`/settings`);
  },
  getVideos: (repairOrderId) => {
    return makeGetRequest(`/repair-order-video?repairOrderID=${repairOrderId}`);
  },
  getMpi: (repairOrderId) => {
    return makeGetRequest(`/repair-order-multi-point-inspection?repairOrderId=${repairOrderId}`);
  },
  getCoupons: () => {
    return makeGetRequest(`/coupons`);
  },
  getPayment: (linkHash) => {
    return makeGetRequest(`/payment/link-hash/${linkHash}`);
  },
  getRewardsTiers: () => {
    return makeGetRequest(`/rewards-tier`);
  },
  getRoPayment: (paymentId) => {
    return makeGetRequest(`/repair-order-payment/${paymentId}`);
  },
  getRoPayments: (repairOrderID) => {
    return makeGetRequest(`/repair-order-payment?repairOrderId=${repairOrderID}`);
  },
  getNonRoPayment: (paymentId) => {
    return makeGetRequest(`${ENDPOINTS.paymentsNonRO}/${paymentId}`);
  },
  getConditions: () => {
    return new Promise((resolve) =>
      resolve({
        status: 200,
        results: [
          {
            name: 'Fair',
            description:
              '18% of cars valued. Has some cosmetic defects that require repairing and/or replacing',
          },
          {
            name: 'Good',
            description:
              '54% of cars valued. Has some repairable cosmetic defects and is free of major mechanical problems',
          },
          {
            name: 'Very Good',
            description:
              '23% of cars valued. Has minor cosmetic defects and is in excellent mechanical condition',
          },
          {
            name: 'Excellent',
            description: '3% of cars valued. Looks new and is in excellent mechanical condition',
          },
        ],
      }),
    );
  },
  getTrims: () => {
    return new Promise((resolve) =>
      resolve({
        status: 200,
        results: [
          {
            name: 'Lexus RX RX 350L Sport Utility 4D AWD',
            vehicleId: 432172,
            vehicleOptionId: 8178725,
          },
          {
            name: 'Lexus EX 150L All Terrain Utility 4D AWD',
            vehicleId: 432172,
            vehicleOptionId: 8178725,
          },
          {
            name: 'Lexus MX  250L Sportsball 2D AWD',
            vehicleId: 432172,
            vehicleOptionId: 8178725,
          },
          {
            name: 'Lexus FX 50L Truck 3D AWD',
            vehicleId: 432172,
            vehicleOptionId: 8178725,
          },
          {
            name: 'Lexus DMX  450L Lambo 2 Tha Moon',
            vehicleId: 432172,
            vehicleOptionId: 8178725,
          },
        ],
      }),
    );
  },
  requestCashOffer: () => {
    return new Promise((resolve) => resolve({ status: 200 }));
  },
  makeRoPayment: (body) => {
    return makePostRequest(`/repair-order-payment/pay`, body);
  },
  makeNonRoPayment: (body) => {
    return makePostRequest(`${ENDPOINTS.paymentsNonRO}/pay`, body);
  },
  // errors in the "viewed" requests should not be seen by the user
  viewedMpi: (repairOrderMPIID) => {
    return makePostRequest('/repair-order-mpi/view', { repairOrderMPIID });
  },
  viewedQuote: (repairOrderQuoteID, eTag) => {
    return makePostRequest(
      '/repair-order-quote/view',
      {
        repairOrderQuoteID,
      },
      {
        headers: setETagHeader(eTag),
      },
    );
  },
  getQuote: (id) => {
    return makeGetRequest(`/repair-order-quote/${id}`);
  },
  updateQuote: (id, body, etag) => {
    return makePutRequest(`/repair-order-quote/${id}`, body, {
      headers: setETagHeader(etag),
    });
  },
  completeQuote: (repairOrderQuoteID, recommendations, etag, signature) => {
    return makePostRequest(
      '/repair-order-quote/complete',
      {
        repairOrderQuoteID,
        recommendations,
        signature,
      },
      {
        headers: setETagHeader(etag),
      },
    );
  },
  viewNonRoPayment: (body) => {
    return makePostRequest(`${ENDPOINTS.paymentsNonRO}/view`, body);
  },
  viewedReview: (repairOrderReviewID) => {
    return makePostRequest(`/my-review/view`, {
      repairOrderReviewID,
    });
  },
  viewedVideo: (repairOrderVideoID) => {
    return makePostRequest(`/repair-order-video/view`, {
      repairOrderVideoID,
    });
  },
  viewedWaiver: (repairOrderId) => {
    return makePostRequest(`/repair-order-waiver/viewed`, {
      repairOrderId,
    });
  },
  sendReceipt: (repairOrderPaymentId, email) => {
    return makePostRequest('/repair-order-payment/send-receipt', {
      repairOrderPaymentId,
      email,
    });
  },
  getAppointmentGroup: () => {
    return makeGetRequest('/appointment-code-group', {});
  },
  getAppointment: (url, params = {}) => {
    return makeGetRequest(url, params);
  },
  getTraxtionScan: (params = {}) => {
    return makeGetRequest('/traxtion/scan', params);
  },
  getGoodyearMpi: (params = {}) => {
    return makeGetRequest('/repair-order-multi-point-inspection', params);
  },
  getSignedDocuments: (params = {}) => {
    return makeGetRequest(ENDPOINTS.esignDocument, params);
  },
  // ISA-4781: with signedDocumentCodeVerification on, the API requires the grant from
  // verifySignedDocumentCode. It travels as a header, never a query param, so it stays out of
  // access logs.
  getSignedDocument: (params = {}, id, grant) => {
    const config = grant ? { headers: { [SIGNED_DOCUMENT_GRANT_HEADER]: grant } } : undefined;
    return makeGetRequest(`${ENDPOINTS.esignDocument}/${id}`, params, config);
  },
  getSignedDocumentRecipients: (repairOrderId) => {
    return makeGetRequest(`${ENDPOINTS.signedDocumentVerification}/options`, { repairOrderId });
  },
  sendSignedDocumentCode: (repairOrderId, customerId) => {
    return makePostRequest(`${ENDPOINTS.signedDocumentVerification}/send`, {
      repairOrderId,
      customerId,
    });
  },
  verifySignedDocumentCode: (repairOrderId, code) => {
    return makePostRequest(`${ENDPOINTS.signedDocumentVerification}/verify`, {
      repairOrderId,
      code,
    });
  },
};

async function makePostRequest(endpoint, data, _config) {
  const body = qs.stringify(data);
  const config = { ..._config };
  const customerAppToken = localStorage.getItem('iservice_customer_app_token');

  if (customerAppToken && !endpoint.includes('authentication')) {
    config.headers = { Authorization: `Bearer ${customerAppToken}`, ...config.headers };
  }

  return axios.post(endpoint, body, config);
}

async function newMakePostRequest(endpoint, data) {
  const config = {};
  const customerAppToken = localStorage.getItem('iservice_customer_app_token');

  if (customerAppToken && !endpoint.includes('authentication')) {
    config.headers = { Authorization: `Bearer ${customerAppToken}` };
  }

  if (data instanceof FormData) {
    return axios.post(endpoint, data, config);
  }

  const formData = new FormData();
  for (const key in data) {
    if (Array.isArray(data[key])) {
      formData.append(key, JSON.stringify(data[key]));
    } else {
      formData.append(key, data[key]);
    }
  }

  const response = await fetch(`${clientApi.getBaseUrl()}${endpoint}`, {
    method: 'POST',
    body: formData,
    headers: config.headers,
  });

  return response.json();
}

async function makePutRequest(endpoint, data, _config) {
  const body = qs.stringify(data);
  const config = { ..._config };
  const customerAppToken = localStorage.getItem('iservice_customer_app_token');

  if (customerAppToken) {
    config.headers = { Authorization: `Bearer ${customerAppToken}`, ...config.headers };
  }

  return axios.put(endpoint, body, config);
}

async function makeGetRequest(endpoint, params, _config) {
  const clientAppToken = localStorage.getItem('iservice_client_app_token');
  const customerAppToken = localStorage.getItem('iservice_customer_app_token');
  const appToken = customerAppToken ? customerAppToken : clientAppToken;
  const config = { ..._config };

  if (appToken) {
    config.headers = { Authorization: `Bearer ${appToken}`, ...config.headers };
  }

  if (params) {
    config.params = params;
  }

  return await axios.get(endpoint, config);
}

export default clientApi;
